import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Database, CalendarRange } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';

function formatDateTime(value) {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleString();
}

function getStatusClass(status) {
  if (status === 'completed') return 'bg-emerald-500/15 text-emerald-300';
  if (status === 'failed') return 'bg-rose-500/15 text-rose-300';
  if (status === 'stopped') return 'bg-amber-500/15 text-amber-300';
  return 'bg-sky-500/15 text-sky-300';
}

export default function FetchLogsPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await api.getFetchHistory();
      setLogs(res.logs || []);
    } catch (err) {
      toast.error('Failed to load fetch logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Fetch Logs</h1>
          <p className="text-sm text-muted-foreground mt-1">History of record fetch jobs with date ranges and fetched counts</p>
        </div>
        <Button size="sm" variant="ghost" onClick={loadLogs}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="fetch-logs-table">
        <div className="border-b border-border px-4 py-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Database className="w-4 h-4" />
          <span>{logs.length} log entries</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Fetched Date</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">From Date</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">To Date</th>
                <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Records Fetched</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide px-4 py-3">Requested By</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-sm text-muted-foreground">
                    No fetch logs yet
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="border-b border-border/50 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <CalendarRange className="w-3.5 h-3.5 text-muted-foreground" />
                        {formatDateTime(log.started_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{formatDateTime(log.from_date)}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{formatDateTime(log.to_date)}</td>
                    <td className="px-4 py-3 text-sm text-right tabular-nums">{log.fetched_count ?? 0}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`text-xs px-2 py-0.5 rounded ${getStatusClass(log.status)}`}>
                        {log.status}
                      </span>
                      {log.error && <p className="text-[11px] text-rose-300 mt-1 max-w-[280px] truncate" title={log.error}>{log.error}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{log.requested_by_name || log.requested_by_email || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
